import { Paging } from 'api/inc-api.types';
import {
  Channels,
  ChannelTemplateSetMap,
  DistributionMode,
  EndDateType,
  Operator,
  RedistributionMode,
  ScheduleDays,
  TimeSlot,
} from 'api/offers-domain/offers-domain.types';

export const messageTypes = {
  SURVEY: 'SURVEY',
  PRIVATE: 'PRIVATE',
  SHAREABLE: 'SHAREABLE',
  REFERRAL: 'REFERRAL',
} as const;

export type MessageType = keyof typeof messageTypes;

export enum MessageStatusType {
  ACTIVE = 'ACTIVE',
  SCHEDULED = 'SCHEDULED',
  EXPIRED = 'EXPIRED',
  DRAFT = 'DRAFT',
  ALL = 'ALL',
}

type MessageDisplayInfo = {
  languageCode: string;
  title: string;
  shortDescription: string;
  longDescription: string;
  smallImage: string;
  mediumImage: string;
  shareTitle?: string;
  shareSentence?: string;
  shareShortDesc?: string;
  shareURL?: string;
};

type MessageBase = {
  messageType: MessageType;
  merchantId: string;
  programId: string;
  campaignId: string;
  title: string;
  createdDate: string;
  updatedDate: string;
  noDistributed: number;
  messageDisplayInfo: MessageDisplayInfo[];
  surveyId: string;
  activityNames: string[];
  reach: string;
  deleted: boolean;
  messageStatus: MessageStatusType;
};

export type PublishedMessage = MessageBase & {
  messageId: string;
  isDraft?: false;
  draftMessageId?: never;
};

export type DraftMessage = MessageBase & {
  messageId: null;
  isDraft: true;
  draftMessageId: string;
};

export type Message = PublishedMessage | DraftMessage;

export type ListMessagesResult = {
  paging: Paging;
  messages: Message[];
};

export type CreateMessageBody = {
  campaignId: string;
  programId: string;
  templateId?: string;
  activityNames: string[];
  adminId: string;
  displayInfo: MessageDisplayInfo[];
  distributionStores: string[];
  distributionSettings: {
    channels: Channels[];
    distributionEndDate: string | null;
    distributionEndDateType: EndDateType | null;
    distributionMode: DistributionMode;
    distributionRules: {
      ruleType: string;
      expressionName: string;
      operator: Operator;
      expressionValue: string | boolean;
      extendedAttributes?: {
        attributeTitle: string;
      };
    };
    distributionSchedule: {
      scheduleDays: ScheduleDays[];
      timeSlots: TimeSlot[];
    };
    distributionStartDate: string;
    numMessagesToDistribute: number | string;
    timezone: string;
    redistributionMode: RedistributionMode;
    channelTemplateSetMap?: ChannelTemplateSetMap;
  };
  enableReach: boolean;
  messageType: MessageType;
  title: string;
  surveyId?: string | null;
  reach?: string | null;
};

export type UpdateMessageBody = CreateMessageBody;

export type GetMessageResult = {
  messageType: MessageType;
  merchantId: string;
  programId: string;
  campaignId: string;
  messageId: string;
  title: string;
  createdDate: string;
  updatedDate: string;
  noDistributed: number | null;
  messageDisplayInfo: MessageDisplayInfo[];
  surveyId: string;
  activityNames: string[];
  reach: string | null;
  deleted: boolean;
  enableReach: boolean;
  distributionStores: string[];
  templateId: string;
  channels?: Channels[];
  channelTemplateSetMap?: ChannelTemplateSetMap;
  distributionMode?: DistributionMode;
  redistributionMode?: RedistributionMode;
  distributionStartDate?: string;
  distributionEndDate?: string | null;
  distributionEndDateType?: EndDateType | null;
  distributionSchedule?: {
    startDate: string;
    endDate: string | null;
    scheduleDays: ScheduleDays[];
    timeSlots: TimeSlot[];
  };
  numMessagesToDistribute?: number;
  timezone?: string;
  ruleSet?: string;
  personaId?: string | null;
  reachDisplayInfo?: string | null;
};

export type GetMessageDistributionSettingsResult = {
  channels: Channels[];
  channelTemplateSetMap: ChannelTemplateSetMap;
  distributionMode: DistributionMode;
  redistributionMode: RedistributionMode;
  distributionStartDate: string;
  distributionEndDate: string | null;
  distributionEndDateType: EndDateType | null;
  distributionSchedule: {
    startDate: string;
    endDate: string | null;
    scheduleDays: ScheduleDays[];
    timeSlots: TimeSlot[];
  };
  numMessagesToDistribute: number;
  timezone: string;
};

export type GetMessageDistributionRulesResult = {
  messageId: string;
  ruleSet: string;
  personaId: string | null;
  enableReach: boolean;
  reach: string | null;
  reachDisplayInfo: string | null;
};

export type CreateDraftMessageBody = CreateMessageBody & {
  campaignId: string;
  programId: string;
};

export type UpdateDraftMessageBody = Omit<
  CreateMessageBody,
  'campaignId' | 'programId'
> & {
  draftMessageId: string;
  campaignId?: string;
  programId?: string;
};

export type GetDraftMessageResult = GetMessageResult & {
  draftMessageId: string;
  isDraft: true;
};

export type PublishDraftMessageResult = {
  messageId: string;
};

export type MessageParamsType = {
  count: number;
  page: number;
  campaignid?: string;
  messagestatus?: MessageStatusType[];
  sortBy?: string;
  sortDirection?: string;
  title?: string;
};
